import { useMemo } from "react";
import { Line } from "react-chartjs-2";
import { ChartData, ChartOptions } from "chart.js";
import useIsMobile from "hooks/useIsMobile";
import LegendItem from "./LegendItem";
import { ChartsData, CHART_COLORS } from "utils/analytics";
import Card from "components/Card";
import RefreshIndicator from "components/RefreshIndicator";
import EmptyTableState from "./shared/components/EmptyTableState";
import {
  BASE_LINE_CHART_OPTIONS,
  CHART_TICK_COLOR,
  CHART_GRID_COLOR,
  createLineDataset,
} from "./charts/lineChart";

const LineChart = ({
  chartOptions,
  chartData,
}: {
  chartOptions: ChartOptions<"line">;
  chartData: ChartData<"line", ChartsData[]>;
}) => (
  <div className="relative w-full h-96">
    <Line options={chartOptions} data={chartData} />
  </div>
);

const ChartSkeleton = () => (
  <div className="relative w-full h-96 overflow-hidden">
    <div className="absolute left-0 top-0 h-full flex flex-col justify-between py-4">
      {[...Array(6)].map((_, i) => (
        <div key={i} className="w-12 h-3 bg-gray-200 rounded animate-pulse" />
      ))}
    </div>
    <div className="ml-16 mr-16 h-full relative">
      <div className="absolute inset-0 flex flex-col justify-between py-4">
        {[...Array(6)].map((_, i) => (
          <div key={i} className="w-full h-px bg-gray-100" />
        ))}
      </div>
      <svg className="absolute inset-0 w-full h-full">
        <path
          d="M 0 200 Q 100 150 200 180 T 400 160 T 600 140 T 800 170"
          fill="none"
          stroke="#e5e7eb"
          strokeWidth="2"
          className="animate-pulse"
        />
        <path
          d="M 0 280 Q 100 260 200 270 T 400 250 T 600 260 T 800 240"
          fill="none"
          stroke="#e5e7eb"
          strokeWidth="2"
          className="animate-pulse"
        />
        <path
          d="M 0 150 Q 100 130 200 140 T 400 120 T 600 130 T 800 110"
          fill="none"
          stroke="#e5e7eb"
          strokeWidth="2"
          className="animate-pulse"
        />
      </svg>
    </div>
    <div className="absolute bottom-0 left-16 right-16 flex justify-between pb-2">
      {[...Array(5)].map((_, i) => (
        <div key={i} className="w-12 h-3 bg-gray-200 rounded animate-pulse" />
      ))}
    </div>
    <div className="absolute right-0 top-0 h-full flex flex-col justify-between py-4">
      {[...Array(6)].map((_, i) => (
        <div key={i} className="w-8 h-3 bg-gray-200 rounded animate-pulse" />
      ))}
    </div>
  </div>
);

const legendList = [
  { label: "Successful requests", color: CHART_COLORS.SUCCESS },
  { label: "Failed requests", color: CHART_COLORS.FAILURE },
  { label: "Concurrent threads", color: CHART_COLORS.CONCURRENT_THREADS },
];

const UsageLineChart = ({
  chartData,
  isLoading = false,
  error = null,
  refetch,
  lastUpdated,
}: {
  chartData: ChartsData[];
  isLoading?: boolean;
  error?: string | null;
  refetch?: () => void | Promise<void>;
  lastUpdated?: Date | null;
}) => {
  const isMobile = useIsMobile();

  // The server already returns one row per day; only concurrency needs rounding
  const chartRows = useMemo(
    () => chartData.map((row) => ({ ...row, concurrentThreads: Math.round(row.concurrentThreads) })),
    [chartData]
  );

  const chartOptions: ChartOptions<"line"> = useMemo(
    () => ({
      ...BASE_LINE_CHART_OPTIONS,
      layout: { padding: 0 },
      parsing: { xAxisKey: "startOfInterval" },
      scales: {
        x: {
          type: "time",
          time: { unit: "day", stepSize: 1, tooltipFormat: "PP" },
          ticks: {
            color: CHART_TICK_COLOR,
            font: { family: "inherit", size: 12 },
          },
        },
        y: {
          type: "linear",
          position: "left",
          ticks: {
            precision: 0,
            color: CHART_TICK_COLOR,
            font: { family: "inherit", size: 12 },
          },
          grid: { color: CHART_GRID_COLOR },
        },
        y2: {
          type: "linear",
          position: "right",
          grid: { drawOnChartArea: false },
          ticks: {
            precision: 0,
            color: CHART_TICK_COLOR,
            font: { family: "inherit", size: 12 },
          },
        },
      },
    }),
    []
  );

  const chartDataObject: ChartData<"line", ChartsData[]> = useMemo(
    () => ({
      datasets: [
        createLineDataset({
          label: "Concurrent Threads",
          color: CHART_COLORS.CONCURRENT_THREADS,
          data: chartRows,
          parsing: { yAxisKey: "concurrentThreads" },
          yAxisID: "y2",
        }),
        createLineDataset({
          label: "Failed requests",
          color: CHART_COLORS.FAILURE,
          data: chartRows,
          parsing: { yAxisKey: "failure" },
          yAxisID: "y",
        }),
        createLineDataset({
          label: "Successful requests",
          color: CHART_COLORS.SUCCESS,
          data: chartRows,
          parsing: { yAxisKey: "success" },
          yAxisID: "y",
        }),
      ],
    }),
    [chartRows]
  );

  const hasData = chartRows.length > 0;

  return (
    <Card className="bg-white border rounded-lg p-6">
      <h1 className="text-xl font-medium">Monitoring</h1>
      <div className={`flex lg:flex-row flex-col gap-4 w-full`}>
        <div
          className={`flex flex-row flex-wrap justify-start md:flex-col md:flex-nowrap gap-4`}
        >
          {legendList.map(({ label, color }) =>
            isLoading ? (
              <div key={label} className="flex items-center gap-2">
                <div className="w-3 h-3 bg-gray-200 rounded animate-pulse" />
                <span className="text-gray-400">{label}</span>
              </div>
            ) : (
              <LegendItem
                key={label}
                color={color}
                label={label}
                isMobile={isMobile}
              />
            )
          )}
          {!isLoading && (
            <RefreshIndicator
              updatedAt={lastUpdated ?? undefined}
              refresh={refetch}
              isRefreshing={isLoading}
              hasError={!!error}
            />
          )}
        </div>
        <div className="flex-1 min-w-0">
          {isLoading ? (
            <ChartSkeleton />
          ) : error ? (
            <div className="border rounded-lg">
              <EmptyTableState
                type="analytics"
                error={true}
                onRetry={refetch}
              />
            </div>
          ) : hasData ? (
            <LineChart
              chartOptions={chartOptions}
              chartData={chartDataObject}
            />
          ) : (
            <div className="border rounded-lg">
              <EmptyTableState type="analytics" />
            </div>
          )}
        </div>
      </div>
    </Card>
  );
};

export default UsageLineChart;
